<template>
  <CheckoutHeader :current-step="1" />
  <div class="relative overflow-hidden max-h-[100dvh] md:max-h-none md:min-h-[calc(100vh-81px)]">
    <BaseContainer>
      <div ref="container">
        <div
          class="transition-all duration-500 ease-in-out"
          :style="{
            paddingRight: open ? `${openOffset}px` : '0',
          }"
        >
          <CheckoutLayoutHeader class="mt-0 md:mt-0" :title="$t('checkout.choose_seats')" :back-label="backLabel" :on-back="handleBack">
            <CheckoutTicketHeader :eventItem="eventItem" :hideMobileDetails="true" />
          </CheckoutLayoutHeader>

          <!-- Multi-event progress indicator -->
          <div
            v-if="totalEventsWithSeatmaps > 1"
            class="mb-2 text-sm font-semibold text-text"
          >
            {{ $t('checkout.event_seatmap_progress', {
                current: currentEventSeatmapNumber,
                total: totalEventsWithSeatmaps,
              }) }}
          </div>

          <!-- Multi-seatmap progress indicator -->
          <div
            v-if="seatmapStore.seatmapQueue.length > 1"
            class="mb-4 text-sm text-text"
          >
            {{ $t('checkout.seatmap_progress', {
                current: seatmapStore.currentSeatmapIndex + 1,
                total: seatmapStore.seatmapQueue.length,
                name: currentQueueItem?.name ?? ''
              }) }}
          </div>

          <div class="relative overflow-hidden rounded-md border border-surface-grey w-full p-1">
            <!-- Help button -->
            <div class="flex gap-2 absolute top-7 left-7 z-10">
              <BaseButton color="muted" size="sm" @click="helpModal = true">
                {{ $t('checkout.seatmap_help_title') }}
              </BaseButton>
              <BaseButton v-if="currentMapHasPriceGroups" color="muted" size="sm" @click="pricesModal = true">
                {{ $t('checkout.seatmap_prices_title') }}
              </BaseButton>
            </div>

             <!-- Canvas area -->
            <div class="flex justify-center">
              <div class="w-full">
                <div v-if="!isCurrentMapLoaded" class="flex justify-center items-center h-64 text-text">
                  {{ $t('common.loading') }}…
                </div>
                <SeatmapCanvas
                  v-else-if="currentQueueItem"
                  :name="currentQueueItem.name"
                  :count="currentQueueItem.count"
                />
              </div>
            </div>
          </div>

        </div>
      </div>
    </BaseContainer>

    <!-- Mobile bottom bar -->
    <transition name="slidedown">
      <div
        class="fixed md:hidden bottom-0 left-0 right-0 z-20 bg-surface-light"
        v-if="!open"
      >
        <!-- Seats remaining strip -->
        <div
          v-if="!isCurrentMapComplete && (currentQueueItem?.count ?? 0) - currentSeatsOurs.length > 0"
          class="flex items-center justify-center gap-1 font-semibold text-sm bg-surface-grey-light border-t border-surface-grey px-4 py-2"
        >
          <SeatInfoIcon class="fill-primary w-6 h-6" />
          {{ (currentQueueItem?.count ?? 0) - currentSeatsOurs.length == 1
            ? $t('checkout.seats_remaining_one')
            : $t('checkout.seats_remaining', { count: (currentQueueItem?.count ?? 0) - currentSeatsOurs.length }) }}
        </div>

        <div class="flex gap-2 justify-between items-center p-4 border-t border-surface-grey">
          <BaseButton
            color="muted"
            size="lg"
            :disabled="currentSeatsOurs.length == 0"
            @click="seatmapStore.resetCurrentSeatmap()"
          >
            {{ $t('checkout.reset_seats_long') }}
          </BaseButton>

          <BaseButton
            class="flex-1"
            color="primary"
            size="lg"
            :disabled="!isCurrentMapComplete || validating"
            @click="open = true"
          >
            {{ $t('common.continue') }}
            <ArrowRightIcon class="fill-current h-4 w-4 ml-2" />
          </BaseButton>
        </div>
      </div>
    </transition>

    <!-- Sidebar panel -->
    <transition name="slidein">
      <div
        class="flex flex-col w-full md:max-w-94 fixed md:fixed right-0 top-0 md:top-[81px] bottom-0 shadow-xl overflow-auto bg-surface-light z-50"
        v-if="open"
      >
        <div
          :class="['flex items-center justify-between sticky top-0 border-b border-surface-grey py-4 text-lg font-semibold bg-surface-light z-10', isEmbed() ? 'px-4' : 'px-6']"
        >
          {{ $t('checkout.your_seats') }}

          <BaseButton
            v-if="isEmbed()"
            color="muted"
            size="lg"
            @click="open = false"
          >
            <DropdownIcon class="h-4 w-4 fill-current rotate-90" />
            {{ $t('common.back') }}
          </BaseButton>
          <button
            v-else
            @click="open = false"
            type="button"
            :title="$t('common.close')"
            class="cursor-pointer hover:opacity-50 md:hidden"
          >
            <CloseIcon class="h-6 w-6" />
          </button>
        </div>

        <div :class="[isEmbed() ? 'px-4 py-6' : 'p-2', 'mb-4 grow overflow-y-auto']">
          <div :class="[isEmbed() ? 'px-0' : 'p-2', 'font-semibold']">{{ getLocalizedEventValue(checkoutStore.event, locale, 'info1') }}</div>
          <div class="rounded-md bg-surface-grey-light p-4">
            <div class="flex items-center gap-4 mb-2">
              <div class="text-xs text-text uppercase">
                {{ $t('checkout.seats') }}
              </div>
              <div class="h-px bg-surface-grey flex-1"></div>
            </div>

            <!-- Placeholder rows when no seats selected yet -->
            <div
              v-if="currentSeatsOurs.length == 0"
              class="text-sm text-text opacity-60"
            >
              {{ $t('checkout.no_seats_selected') }}
            </div>

            <!-- Selected seats grouped by ticket type -->
            <div
              v-for="group in ticketSeatGroups"
              :key="group.ticketId"
              class="mb-3"
            >
              <div class="font-semibold mb-1">{{ getTicketNameForTicketId(group.ticketId) }}</div>

              <div
                class="flex w-full justify-between mb-2"
                v-for="seat in group.seats"
                :key="seat"
              >
                <div class="flex-1">
                  <template v-if="showSeatDropdown(group.ticketId)">
                    <InputSelect
                      :model-value="String(seat)"
                      @update:model-value="(val: string) => handleSeatSwap(seat, val)"
                      :options="getSeatOptionsForTicket(group.ticketId)"
                      :name="'seat-pick-' + seat"
                      :id="'seat-pick-' + seat"
                      :hide-title="true"
                      :truncate="true"
                    />
                  </template>
                  <template v-else>
                    <div class="text-xs text-text">{{ seatLabel(seat) }}</div>
                    <div v-if="getSeatPriceGroupName(seat)" class="text-xs text-text opacity-70">{{ getSeatPriceGroupName(seat) }}</div>
                  </template>
                </div>
                <div v-if="!showSeatDropdown(group.ticketId)" class="font-semibold">
                  {{ getSeatPrice(seat) }}
                </div>
              </div>
            </div>
          </div>
          <button
            v-if="currentSeatsOurs.length > 0"
            type="button"
            class="w-full mt-2 text-sm text-text underline cursor-pointer text-left"
            @click="seatmapStore.resetCurrentSeatmap()"
          >
            {{ $t('checkout.reset_seats') }}
          </button>
        </div>

        <div class="p-6 border-t border-surface-grey">
          <div v-if="currentSeatsOurs.length > 0" class="flex justify-between text-lg font-semibold mb-4">
            <span>{{ $t('common.total') }}</span>
            <span>{{ formatPrice(seatSelectionTotal, checkoutStore.event.currency, locale) }}</span>
          </div>
          <BaseButton
            class="w-full"
            color="primary"
            size="lg"
            :disabled="!isCurrentMapComplete || validating"
            @click="handleContinue"
          >
            {{ $t('common.continue') }}
            <ArrowRightIcon class="fill-current h-4 w-4 ml-2" />
          </BaseButton>
        </div>
      </div>
    </transition>

    <!-- Help modal -->
    <BaseModal v-model="helpModal" size="md">
      <template #header>{{ $t('checkout.seatmap_help_title') }}</template>
      <div class="p-6">
        <p class="mb-3">{{ $t('checkout.seatmap_help_body_intro') }}</p>
        <p class="font-bold">{{ $t('checkout.seatmap_help_body_colors_title') }}</p>
        <p>{{ $t('checkout.seatmap_help_body_colors_content') }}</p>
      </div>
    </BaseModal>

    <!-- Prices legend modal -->
    <BaseModal v-model="pricesModal" size="md">
      <template #header>{{ $t('checkout.seatmap_prices_title') }}</template>
      <div class="p-6">
        <div
          v-for="ticketGroup in priceGroupLegend"
          :key="ticketGroup.ticketId"
          class="mb-5 last:mb-0"
        >
          <div
            v-if="priceGroupLegend.length > 1"
            class="text-xs font-semibold uppercase text-text opacity-60 mb-2"
          >
            {{ ticketGroup.ticketName }}
          </div>
          <div
            v-for="entry in ticketGroup.entries"
            :key="`${ticketGroup.ticketId}-${entry.id}`"
            class="flex items-center gap-3 mb-3 last:mb-0"
          >
            <span
              class="inline-block w-5 h-5 rounded-full shrink-0"
              :style="{ backgroundColor: '#' + entry.color }"
            />
            <span class="flex-1 text-sm">{{ entry.name }}</span>
            <span class="text-sm font-semibold whitespace-nowrap">
              {{ formatPrice(entry.price, checkoutStore.event.currency, locale) }}
            </span>
          </div>
        </div>
        <div v-if="priceGroupLegend.length == 0" class="text-sm text-text opacity-60">
          {{ $t('common.loading') }}…
        </div>
      </div>
    </BaseModal>
  </div>
</template>

<script setup lang="ts">
  import { useHead } from '@unhead/vue';
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  import { fetchEvent } from '@/api/event';
  import CheckoutHeader from '@/components/checkout/CheckoutHeader.vue';
  import CheckoutLayoutHeader from '@/components/checkout/CheckoutLayoutHeader.vue';
  import CheckoutTicketHeader from '@/components/checkout/CheckoutTicketHeader.vue';
  import SeatmapCanvas from '@/components/checkout/SeatmapCanvas.vue';
  import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue';
  import CloseIcon from '@/components/icons/CloseIcon.vue';
  import DropdownIcon from '@/components/icons/DropdownIcon.vue';
  import SeatInfoIcon from '@/components/icons/SeatInfoIcon.vue';
  import BaseButton from '@/components/ui/BaseButton.vue';
  import BaseContainer from '@/components/ui/BaseContainer.vue';
  import BaseModal from '@/components/ui/BaseModal.vue';
  import InputSelect from '@/components/ui/inputs/InputSelect.vue';
  import { useCartStore } from '@/stores/cart';
  import { useCheckoutStore } from '@/stores/checkoutStore';
  import { useSeatmapStore } from '@/stores/seatmapStore';
  import { useToastStore } from '@/stores/toastStore';
  import type { EventDetail } from '@/types';
  import { useCheckoutNavigation } from '@/utils/checkoutNavigation';
  import { getLocalizedEventValue } from '@/utils/eventItemUtil';
  import { formatPrice } from '@/utils/formatPrice';
  import { isEmbed } from '@/utils/isEmbed';
  import { buildPriceGroupColorList } from '@/utils/seatmap/canvas/priceGroupColors';
  import { getLocalizedTicketValue } from '@/utils/ticketItemUtil';
  import { useWebsocketStore } from '@/websocket/websocket';

  const { t, locale } = useI18n();

  const helpModal = ref(false);
  const pricesModal = ref(false);
  const cartStore = useCartStore();
  const checkoutStore = useCheckoutStore();
  const seatmapStore = useSeatmapStore();
  const toastStore = useToastStore();
  const websocketStore = useWebsocketStore();
  const router = useRouter();
  const { navigateToNext, getStepPath } = useCheckoutNavigation();

  function handleBack() {
    router.push(getStepPath('checkout-tickets'));
  }

  useHead({
    title: t('checkout.choose_seats'),
    meta: [{ property: 'og:title', content: t('checkout.choose_seats') }],
  });

  // ─── Event detail (for header) ───────────────────────────────────────────────

  const eventItem = ref<EventDetail>({} as EventDetail);
  const eventID = computed(() => checkoutStore.getCheckoutID());
  function loadEventDetail(id: number | string) {
    fetchEvent(id).then(data => {
      eventItem.value = data || ({} as EventDetail);
      if (data?.seatmap_max_distance) {
        seatmapStore.setMaxDistance(data.seatmap_max_distance);
      }
    });
  }
  if (eventID.value) {
    loadEventDetail(eventID.value);
  }
  // Re-fetch event detail when switching to a different event (multi-event basket)
  watch(eventID, (newId) => {
    if (newId) loadEventDetail(newId);
  });

  // ─── Current queue item helpers ──────────────────────────────────────────────

  const currentQueueItem = computed(() => seatmapStore.currentQueueItem);

  const isCurrentMapLoaded = computed(() => {
    const name = currentQueueItem.value?.name;
    return name ? seatmapStore.loadedSeatmaps[name] == true : false;
  });

  const currentSeatsOurs = computed(() => {
    const name = currentQueueItem.value?.name;
    return name ? (seatmapStore.seatStates[name]?.seatsOurs ?? []) : [];
  });

  const isCurrentMapComplete = computed(() =>
    currentQueueItem.value != undefined &&
    currentSeatsOurs.value.length == currentQueueItem.value.count
  );

  const isLastMap = computed(() =>
    seatmapStore.currentSeatmapIndex == seatmapStore.seatmapQueue.length - 1
  );

  // ─── Multi-event seatmap helpers ─────────────────────────────────────────────
// deprecated, can be reused 
  /** The next cart item that has seatmaps but hasn't completed seat selection. */
  /*const nextCartItemNeedingSeats = computed<import('@/types').CartItem | undefined>(() => {
    for (const item of cartStore.items) {
      if (
        seatmapStore.eventsWithSeatmaps[item.id] &&
        !seatmapStore.eventsWithSeatsCompleted[item.id]
      ) {
        // Skip the current event
        if (String(item.id) == String(eventID.value)) continue;
        return item;
      }
    }
    return undefined;
  });*/
  // deprecated, can be reused 
  /** Whether there are more events that still need seat selection. */
  //const hasMoreEventsNeedingSeats = computed(() => nextCartItemNeedingSeats.value != null);

  /** Total number of events in the cart that have seatmaps. */
  const totalEventsWithSeatmaps = computed(() =>
    cartStore.items.filter(item => seatmapStore.eventsWithSeatmaps[item.id]).length
  );

  /** 1-based index of the current event among all events with seatmaps. */
  const currentEventSeatmapNumber = computed(() => {
    const seatmapEventIds = cartStore.items
      .filter(item => seatmapStore.eventsWithSeatmaps[item.id])
      .map(item => item.id);
    const idx = seatmapEventIds.findIndex(id => String(id) == String(eventID.value));
    return idx >= 0 ? idx + 1 : 1;
  });

  /** Returns a localized event name from a CartItem, falling back to item.name. */
  function getLocalizedCartItemName(item: import('@/types').CartItem): string {
    const lang = locale.value;
    return item.localizations?.[lang]?.info1
      || Object.values(item.localizations || {})[0]?.info1
      || item.name;
  }

  const backLabel = computed(() =>
    checkoutStore.hasUpsaleTickets ? t('common.back') : t('checkout.change_number_of_seats')
  );

  // deprecated
  /*const continueLabel = computed(() => {
    if (isLastMap.value && hasMoreEventsNeedingSeats.value) {
      const nextItem = nextCartItemNeedingSeats.value;
      const name = nextItem ? getLocalizedCartItemName(nextItem) : '';
      return t('checkout.next_event_seats', { event: name });
    }
    return isLastMap.value
      ? t('globals.continue_to_checkout')
      : t('checkout.next_seatmap');
  });*/

  // ─── Seat label helper ───────────────────────────────────────────────────────

  function seatLabel(seatIndex: number): string {
    return seatmapStore.getSeatLabel(seatIndex, currentQueueItem.value?.name ?? '');
  }

  // ─── Seat ticket assignment helpers ──────────────────────────────────────────

  // ─── Ticket-grouped seat helpers ─────────────────────────────────────────────

  /** Groups selected seats by their assigned ticket ID, ordered by ticketIds. */
  const ticketSeatGroups = computed(() => {
    const item = currentQueueItem.value;
    if (!item) return [];
    const name = item.name;
    const seatsOurs = currentSeatsOurs.value;
    const assignments = seatmapStore.seatTicketAssignments[name];

    // Use the queue item's ticketIds, falling back to checkoutStore.tickets
    // when the queue was built with empty ticketIds (state corruption recovery).
    let ticketIds = item.ticketIds;
    if (ticketIds.length == 0 && seatsOurs.length > 0) {
      ticketIds = checkoutStore.tickets
        .filter(t => t.seatmap == name)
        .map(t => t.id);
    }

    // Build a map: ticketId → seat indices
    const grouped: Record<number, number[]> = {};
    for (const ticketId of ticketIds) {
      grouped[ticketId] = [];
    }

    // Count how many seatsOurs are covered by explicit assignments
    let assignedCount = 0;
    if (assignments && Object.keys(assignments).length > 0) {
      for (const seat of seatsOurs) {
        const ticketId = assignments[seat];
        if (ticketId != null) {
          (grouped[ticketId] ??= []).push(seat);
          assignedCount++;
        }
      }
    }

    // Fallback to ordinal assignment when assignments are absent OR stale
    // (i.e. none of the current seatsOurs matched the stored assignments).
    if (assignedCount == 0) {
      // No price-groups: assign by ordinal position (mirrors applySeatsToCartItems fallback)
      const cartItem = cartStore.items.find(ci => String(ci.id) == String(eventID.value));
      let offset = 0;
      for (const ticketId of ticketIds) {
        const cartTicket = cartItem?.tickets.find(t => t.id == ticketId);
        const count = cartTicket?.count ?? 0;
        for (let i = offset; i < offset + count && i < seatsOurs.length; i++) {
          grouped[ticketId].push(seatsOurs[i]);
        }
        offset += count;
      }
    }

    // Return in ticketIds order, skipping empty groups
    return ticketIds
      .filter(id => (grouped[id]?.length ?? 0) > 0)
      .map(id => ({ ticketId: id, seats: grouped[id] }));
  });

  /** Look up ticket name directly by ticket ID. */
  function getTicketNameForTicketId(ticketId: number): string {
    const checkoutTicket = checkoutStore.tickets.find(ticket => ticket.id == ticketId);
    return checkoutTicket ? getLocalizedTicketValue(checkoutTicket, locale.value, 'name1') : '';
  }

  /** Returns all selected seats that are eligible for the given ticket ID. */
  function getEligibleSeatsForTicket(ticketId: number): number[] {
    const name = currentQueueItem.value?.name ?? '';
    return currentSeatsOurs.value.filter(seat => {
      const eligible = seatmapStore.getEligibleTicketsForSeat(seat, name);
      return eligible.some(e => e.ticketId == ticketId);
    });
  }

  /** Whether the seat-picker dropdown should be shown for a given ticket (more than one eligible seat across tickets). */
  function showSeatDropdown(ticketId: number): boolean {
    const name = currentQueueItem.value?.name ?? '';
    const assignments = seatmapStore.seatTicketAssignments[name];
    if (!assignments || Object.keys(assignments).length == 0) return false;
    // Show dropdown when seats assigned to this ticket could also go to another ticket
    // (i.e. there are more eligible seats than seats assigned to this ticket)
    const assignedCount = ticketSeatGroups.value.find(g => g.ticketId == ticketId)?.seats.length ?? 0;
    return getEligibleSeatsForTicket(ticketId).length > assignedCount;
  }

  /** Build seat-picker options for a specific ticket + current seat slot. */
  function getSeatOptionsForTicket(ticketId: number): Array<{ label: string; value: string }> {
    const name = currentQueueItem.value?.name ?? '';
    const eligible = getEligibleSeatsForTicket(ticketId);
    return eligible.map(seat => {
      const label = seatLabel(seat);
      const eligibleEntry = seatmapStore.getEligibleTicketsForSeat(seat, name)
        .find(e => e.ticketId == ticketId);
      const price = eligibleEntry
        ? formatPrice(eligibleEntry.group.price, checkoutStore.event.currency, locale.value)
        : getSeatPrice(seat);
      return { label: `${price} | ${label}`, value: String(seat) };
    });
  }

  /** Handle seat swap when user picks a different seat from the dropdown. */
  function handleSeatSwap(currentSeat: number, newSeatValue: string) {
    const newSeat = Number(newSeatValue);
    if (newSeat == currentSeat) return;
    const name = currentQueueItem.value?.name ?? '';
    seatmapStore.swapSeatAssignments(currentSeat, newSeat, name);
  }

  // ─── Per-seat price & price group helpers ────────────────────────────────────

  /** Returns the raw price (in cents) for a seat, used for summing totals. */
  function getSeatPriceRaw(seatIndex: number): number {
    const item = currentQueueItem.value;
    if (!item) return 0;
    const group = seatmapStore.getAssignedSeatPriceGroup(seatIndex, item.name);
    if (group) return group.price;
    const seatsOurs = seatmapStore.seatStates[item.name]?.seatsOurs ?? [];
    const ordinal = seatsOurs.indexOf(seatIndex);
    if (ordinal >= 0) {
      const cartItem = cartStore.items.find(ci => String(ci.id) == String(eventID.value));
      if (cartItem) {
        let offset = 0;
        for (const ticketId of item.ticketIds) {
          const cartTicket = cartItem.tickets.find(t => t.id == ticketId);
          if (!cartTicket) continue;
          if (ordinal < offset + cartTicket.count) {
            const checkoutTicket = checkoutStore.tickets.find(ticket => ticket.id == ticketId);
            if (checkoutTicket) {
              return checkoutTicket.price_groups.length > 0 ? checkoutTicket.min_price : checkoutTicket.price;
            }
          }
          offset += cartTicket.count;
        }
      }
    }
    const ticketId = item.ticketIds[0];
    const checkoutTicket = checkoutStore.tickets.find(ticket => ticket.id == ticketId);
    if (!checkoutTicket) return 0;
    return checkoutTicket.price_groups.length > 0 ? checkoutTicket.min_price : checkoutTicket.price;
  }

  /** Total price (in cents) of the currently selected seats on this seatmap. */
  const seatSelectionTotal = computed(() =>
    currentSeatsOurs.value.reduce((sum, seat) => sum + getSeatPriceRaw(seat), 0)
  );

  function getSeatPrice(seatIndex: number): string {
    const item = currentQueueItem.value;
    if (!item) return '';
    const group = seatmapStore.getAssignedSeatPriceGroup(seatIndex, item.name);
    if (group) {
      return formatPrice(group.price, checkoutStore.event.currency, locale.value);
    }
    // Fallback: derive ticket by ordinal position in seatsOurs (mirrors getTicketNameForSeat Strategy B)
    const seatsOurs = seatmapStore.seatStates[item.name]?.seatsOurs ?? [];
    const ordinal = seatsOurs.indexOf(seatIndex);
    if (ordinal >= 0) {
      const cartItem = cartStore.items.find(ci => String(ci.id) == String(eventID.value));
      if (cartItem) {
        let offset = 0;
        for (const ticketId of item.ticketIds) {
          const cartTicket = cartItem.tickets.find(t => t.id == ticketId);
          if (!cartTicket) continue;
          if (ordinal < offset + cartTicket.count) {
            const checkoutTicket = checkoutStore.tickets.find(ticket => ticket.id == ticketId);
            if (checkoutTicket) {
              const basePrice = checkoutTicket.price_groups.length > 0 ? checkoutTicket.min_price : checkoutTicket.price;
              return formatPrice(basePrice, checkoutStore.event.currency, locale.value);
            }
          }
          offset += cartTicket.count;
        }
      }
    }
    // Last-resort fallback — use the first ticket (original behaviour)
    const ticketId = item.ticketIds[0];
    const checkoutTicket = checkoutStore.tickets.find(ticket => ticket.id == ticketId);
    if (!checkoutTicket) return '';
    const basePrice = checkoutTicket.price_groups.length > 0 ? checkoutTicket.min_price : checkoutTicket.price;
    return formatPrice(basePrice, checkoutStore.event.currency, locale.value);
  }

  function getSeatPriceGroupName(seatIndex: number): string {
    const item = currentQueueItem.value;
    if (!item) return '';
    return seatmapStore.getAssignedSeatPriceGroup(seatIndex, item.name)?.name ?? '';
  }

  /** True when the current seatmap's tickets have price groups configured. */
  const currentMapHasPriceGroups = computed(() => {
    const item = currentQueueItem.value;
    if (!item) return false;
    return item.ticketIds.some(id => {
      const t = checkoutStore.tickets.find(ticket => ticket.id == id);
      return (t?.price_groups?.length ?? 0) > 0;
    });
  });

  /** Price-group legend entries for the current seatmap, grouped by ticket. */
  const priceGroupLegend = computed(() => {
    const name = currentQueueItem.value?.name;
    if (!name) return [];
    const priceGroups = seatmapStore.seatStates[name]?.priceGroups;
    const entries = buildPriceGroupColorList(priceGroups);

    // Group entries by ticketId, preserving encounter order.
    const grouped = new Map<number, typeof entries>();
    for (const entry of entries) {
      const list = grouped.get(entry.ticketId);
      if (list) {
        list.push(entry);
      } else {
        grouped.set(entry.ticketId, [entry]);
      }
    }

    return [...grouped.entries()].map(([ticketId, ticketEntries]) => ({
      ticketId,
      ticketName: getTicketNameForTicketId(ticketId),
      entries: ticketEntries,
    }));
  });

  // ─── Navigation ──────────────────────────────────────────────────────────────

  const validating = ref(false);

  function doNavigate() {
    if (isLastMap.value) {
      cartStore.applySeatsToCartItems();
      seatmapStore.markEventSeatsCompleted(eventID.value!);
      seatmapStore.snapshotCompletedEvent(eventID.value!);

      // Check if another event in the basket still needs seat selection
      const nextEvent = seatmapStore.getNextEventNeedingSeats(cartStore.items);
      if (nextEvent != null) {
        // Show toast informing the user they're moving to the next event's seatmap
        const nextItem = cartStore.items.find(ci => String(ci.id) == String(nextEvent));
        if (nextItem) {
          toastStore.showToast(t('checkout.switching_to_event', { event: getLocalizedCartItemName(nextItem) }));
        }
        // Switch to the next event and reload seatmaps in-place
        checkoutStore.setCheckoutID(nextEvent);
        websocketStore.send('subscribeevent', { id: nextEvent }, undefined);
        seatmapStore.sendEventseatmaps(nextEvent as number);
        open.value = false;
      } else {
        navigateToNext('checkout-seats');
      }
    } else {
      seatmapStore.advanceToNextSeatmap();
      open.value = false;
      // Send a fresh reserveseats for the newly active map to get current state2
      const nextItem = seatmapStore.currentQueueItem;
      if (nextItem) {
        seatmapStore.sendReserveSeats(nextItem.name, []);
      }
    }
  }

  function handleContinue() {
    if (!isCurrentMapComplete.value || validating.value) return;

    if (!currentMapHasPriceGroups.value) {
      doNavigate();
      return;
    }

    // Reset the flag before sending so a previous result doesn't trigger the watcher.
    seatmapStore.validatePriceGroupsOk = null;
    validating.value = true;
    seatmapStore.sendValidatePriceGroups(eventID.value as number);

    // sendValidatePriceGroups may resolve synchronously (e.g. nothing to validate
    // because priceGroups haven't arrived from WS yet). Handle that case directly
    // so we never set up a watcher whose stop handle doesn't exist yet.
    if (seatmapStore.validatePriceGroupsOk != null) {
      validating.value = false;
      if (seatmapStore.validatePriceGroupsOk == true) {
        doNavigate();
      } else {
        toastStore.showToast(t('checkout.seat_price_group_validation_failed'));
      }
      return;
    }

    // Otherwise wait for the WS validatepricegroups response.
    const stopWatch = watch(
      () => seatmapStore.validatePriceGroupsOk,
      (result) => {
        if (result == null) return; // still waiting
        stopWatch();
        validating.value = false;
        if (result == true) {
          doNavigate();
        } else {
          toastStore.showToast(t('checkout.seat_price_group_validation_failed'));
        }
      },
    );
  }

  // ─── Sidebar & layout ────────────────────────────────────────────────────────

  const open = ref(false);
  const openOffset = ref(0);
  const container = ref<HTMLElement | null>(null);
  const isDesktop = ref(window.innerWidth >= 768);

  const calculateOffset = () => {
    isDesktop.value = window.innerWidth >= 768;
    if (!container.value || !isDesktop.value) {
      openOffset.value = 0;
      return;
    }
    const rect = container.value.getBoundingClientRect();
    openOffset.value = Math.max(375 + 24 - (window.innerWidth - rect.width) / 2, 0);
  };

  // Auto-open sidebar when all seats selected (desktop only).
  // On mobile the user explicitly taps "Continue" in the bottom bar to open it.
  // { immediate: true } ensures the sidebar opens on back-navigation when
  // seats are already fully selected from a previous visit (desktop).
  watch([isCurrentMapComplete, isDesktop], ([complete, desktop]) => {
    if (complete && desktop) {
      open.value = true;
    }
  }, { immediate: true });

  // ─── Lifecycle ───────────────────────────────────────────────────────────────

  onMounted(() => {
    calculateOffset();
    window.addEventListener('resize', calculateOffset);

    // Ask the server which seatmaps the basket requires and how many seats each.
    // The response (eventseatmaps) will populate the queue, load assets, and
    // request initial seat availability via reserveseats automatically.
    const id = eventID.value;
    if (!id) {
      navigateToNext('checkout-seats');
      return;
    }

    // Immediately restore structural state (queue, currentSeatmapIndex,
    // seatTicketAssignments) from localStorage so the UI is usable before the
    // WebSocket responds.  The WS response will then overwrite the queue with
    // authoritative server data and replay seatsOurs via reserveseats.
    seatmapStore.restoreSeatmapState(id);

    // Clear stale seat labels (flexData) for this event so that basket updates
    // arriving while the user re-selects seats don't re-stamp old labels.
    // Fresh labels will be written by applySeatsToCartItems when the user
    // proceeds from the seatmap view.
    cartStore.clearFlexDataForEvent(id);

    seatmapStore.sendEventseatmaps(id as number);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', calculateOffset);
  });
</script>
